import React from "react";
import { shallowEqual } from "react-redux";

import { setToModel } from "../../../Engine/ActionCreators/AllActionCreators";
import { CandyDispatch } from "../../../Engine/Core/Store/CandyReduxTypes";
import { FullFormState } from "../../../Engine/Core/Store/FullFormState";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { withControlDebugInfo } from "../../withControlDebugInfo";
import { PathResolver, withResolvePaths } from "../../withResolvePaths";
import { withViewModelSelect } from "../../withViewModelSelect";
import { withExternalMountingEventReporting } from "../../Services/ExternalMountingEventReporting/withExternalMountingEventReporting";
import { withFocusManagement } from "../../Services/FocusManagement/withFocusManagement";
import { withTestPageObjectRegistration } from "../../Services/TestPageObjects/PageObjectExposure";
import { EvaluableProp, withEvaluators } from "../../withEvaluators";
import {
    AbsoluteResolvedModelPath,
    createAbsoluteResolvedFromTokens,
    createRelativeFromTokens,
    PathTokens,
} from "../../../../../Common/ModelPath/ModelPath";
import {
    FormActionHandlerExecutionInjectableProps,
    withFormActionHandlerExecution,
} from "../../Internals/FormActionHandlers/WithFormActionHandlerExecution";
import { FormActionHandler } from "../../Internals/FormActionHandlers/FormActionHandler";
import { isNotNullOrEmpty, isNotNullOrUndefined, reject } from "../../../../../Common/TypingUtils";
import { Checkbox, CheckboxValue } from "../Checkbox/Checkbox";
import { IterUtils } from "../../../../../Common/IterableUtils";
import { buildDirtyPathForMultiplicityAndCheckboxPath } from "../../FastSelectAllCheckboxesHelper";
import { useTypedDispatch, useTypedSelector } from "../../../../../Common/UseTypedSelector";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { withDisabledByParent } from "../../withDisabledByParent";
import { extractDirtyCheckboxesArray } from "../SelectAllCheckbox/SelectAllCheckboxHelper";

interface SelectCheckboxContainerProps extends FormActionHandlerExecutionInjectableProps, ControlTooltipProps {
    bindingPath: AbsoluteResolvedModelPath;
    viewModelSelect: ViewModelSelect;
    disabled2?: EvaluableProp<boolean>;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    callHelperOnChange: FormActionHandler;
    resolvePath: PathResolver;
    checkedValue?: CheckboxValue;
    uncheckedValue?: CheckboxValue;
    "data-tid"?: string;
}

const getBasePathAndCheckedFieldPath = (bindingPath: AbsoluteResolvedModelPath) => {
    const splitIndex =
        IterUtils.last(
            Iterator.from(bindingPath.getPathParts())
                .asIndexedPairs()
                .filter(([_, value]) => PathTokens.isInstanceToken(value))
                .map(([index]) => index)
        ) ?? reject();
    return {
        basePathWithInstance: createAbsoluteResolvedFromTokens(
            Iterator.from(bindingPath.getPathParts())
                .take(splitIndex + 1)
                .toArray()
        ),
        checkboxFieldPath: createRelativeFromTokens(
            Iterator.from(bindingPath.getPathParts())
                .drop(splitIndex + 1)
                .toArray()
        ),
    };
};

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: SelectCheckboxContainerProps) => {
    const { bindingPath, checkedValue, viewModelSelect } = ownProps;

    const onChangeHandler = async (value: undefined | CheckboxValue) => {
        const hasUpdatedToDirtyArray = dispatch((_, getState, form) => {
            const { basePathWithInstance, checkboxFieldPath } = getBasePathAndCheckedFieldPath(bindingPath);
            const parts = basePathWithInstance.getPathPartsAsArray();
            const dirtyArrayPath = buildDirtyPathForMultiplicityAndCheckboxPath(
                createAbsoluteResolvedFromTokens(parts.slice(0, parts.length - 1)),
                checkboxFieldPath
            );
            const dirtyArrayControlModel = viewModelSelect.controlModel(getState(), dirtyArrayPath)?.value;
            if (isNotNullOrEmpty(dirtyArrayControlModel)) {
                const dirtyArrayControlModelSet = new Set(extractDirtyCheckboxesArray(dirtyArrayControlModel));
                const instanceNumber = IterUtils.last(basePathWithInstance.getPathParts());
                if (instanceNumber != undefined) {
                    const isChecked = value == checkedValue || value == true || value == "true";
                    if (isChecked) {
                        dirtyArrayControlModelSet.add(instanceNumber);
                    } else {
                        dirtyArrayControlModelSet.delete(instanceNumber);
                    }
                    form.setValue(dirtyArrayPath, JSON.stringify([...dirtyArrayControlModelSet.values()]));
                    return true;
                }
            }
            return false;
        });
        if (!hasUpdatedToDirtyArray) {
            dispatch(setToModel(bindingPath, "value", value));
        }
        if (isNotNullOrUndefined(ownProps.callHelperOnChange)) {
            await ownProps.executeFormActionHandler(ownProps.callHelperOnChange);
        }
    };

    return {
        onChange: (value: undefined | CheckboxValue) => onChangeHandler(value),
        setCorrectValue: (value: undefined | CheckboxValue) => onChangeHandler(value),
        setError: (error: boolean) => {
            dispatch(setToModel(bindingPath, "error", error ? [error] : []));
        },
    };
};

const CheckboxWrappedComponent = withTestPageObjectRegistration(
    withFocusManagement(withExternalMountingEventReporting(withControlDebugInfo(Checkbox)))
);

const SelectCheckboxWrapper = (props: SelectCheckboxContainerProps) => {
    const { bindingPath, viewModelSelect, checkedValue, uncheckedValue } = props;
    const { basePathWithInstance, checkboxFieldPath } = getBasePathAndCheckedFieldPath(bindingPath);
    const getDirtyArrayControlModel = (state: FullFormState) => {
        const parts = basePathWithInstance.getPathPartsAsArray();
        const dirtyArrayControlModelPath = buildDirtyPathForMultiplicityAndCheckboxPath(
            createAbsoluteResolvedFromTokens(parts.slice(0, parts.length - 1)),
            checkboxFieldPath
        );
        const dirtyArrayControlModel = viewModelSelect.controlModel(state, dirtyArrayControlModelPath)?.value;
        return dirtyArrayControlModel;
    };

    const stateSlice = useTypedSelector(state => {
        const dirtyArrayValue = getDirtyArrayControlModel(state);
        const controlModel = viewModelSelect.controlModel(state, bindingPath);
        const value = controlModel?.value ?? false;

        return {
            dirtyArrayValue: dirtyArrayValue,
            value: value,
            disabled: props.processEvaluableProp(state, props.disabled2),
            isReadOnly: state.settings.isReadOnly,
            isFormReloading: state.formVisualState.isFormReloading,
        };
    }, shallowEqual);

    const getFinalValue = () => {
        if (isNotNullOrEmpty(stateSlice.dirtyArrayValue)) {
            const dirtyArrayControlModelSet = new Set(extractDirtyCheckboxesArray(stateSlice.dirtyArrayValue));
            const instanceNumber = IterUtils.last(basePathWithInstance.getPathParts()) ?? "0";
            return dirtyArrayControlModelSet.has(instanceNumber) ? (checkedValue ?? true) : (uncheckedValue ?? false);
        }
        return stateSlice.value;
    };
    const finalValue = getFinalValue();

    const dispatch = useTypedDispatch();
    const dispatchProps = mapDispatchToProps(dispatch, props);

    return (
        <CheckboxWrappedComponent
            bindingPath={bindingPath}
            value={finalValue}
            disabled={stateSlice.disabled}
            isReadOnly={stateSlice.isReadOnly}
            isFormReloading={stateSlice.isFormReloading}
            checkedValue={checkedValue}
            uncheckedValue={uncheckedValue}
            tooltip={props.tooltip}
            tooltipWidth={props.tooltipWidth}
            tooltipPosition={props.tooltipPosition}
            {...{ "data-tid": props["data-tid"] }}
            {...dispatchProps}
        />
    );
};

export const SelectCheckboxContainer = withResolvePaths(
    withFormActionHandlerExecution(withDisabledByParent(withViewModelSelect(withEvaluators(SelectCheckboxWrapper))))
);
